import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AGENT_STATE_DEFS,
  TEMPLATE_CHANNEL_MAP,
  fillTemplate,
  AGENT_STATE_MAP,
  AGENT_STATES_MANUAL,
  whatsappNumber,
  type AgentState,
} from "@goyone/shared";
import { api, qs } from "../../lib/api";
import { relative, time } from "../../lib/format";
import { useAuth } from "../../auth/AuthProvider";
import { Icon } from "../../components/Icon";
import { Badge, EmptyState, Modal, PageHead } from "../../components/ui";
import { OwnerChip } from "../../components/People";
import { MailCompose, type MailLink } from "../../components/MailCompose";
import { ReachIcons } from "../../components/Reach";
import {
  AddCallerButton,
  CallDirection,
  identityOf,
  type CallIdentityRow,
} from "../../components/CallIdentity";
import { useToast } from "../../components/Toast";
import { useShiftRecorder } from "../../components/ShiftRecorder";
import { DesktopAppNotice } from "../../components/DesktopAppNotice";
import {
  NO_LINE_MESSAGE,
  phoneStatusLabel,
  usePairedPhone,
  type PairedPhone,
} from "../../lib/phone";
import type { PhoneReply } from "../../lib/desktop";

/**
 * تطبيع الأرقام إلى لاتينية.
 *
 * على كيبورد عربي يُنتج صفّ الأرقام ٠-٩ لا 0-9، و كان يمسحها فلا
 * يُكتب شيء بالكيبورد. هنا تُحوَّل العربية والفارسية إلى لاتينية، ويبقى
 * + والفراغ والشرطة، وسواها يسقط — فالكتابة تعمل من أي كيبورد.
 */
function toAsciiDigits(raw: string): string {
  let out = "";
  for (const ch of raw) {
    const code = ch.codePointAt(0)!;
    if (code >= 0x0660 && code <= 0x0669) out += String(code - 0x0660);
    else if (code >= 0x06f0 && code <= 0x06f9) out += String(code - 0x06f0);
    else if (/[0-9+\s()-]/.test(ch)) out += ch;
  }
  return out;
}

/**
 * كونسول مركز الاتصال — شاشة الموظف.
 *
 * لوحان: لوحة اتصال تُدخل الرقم وتتعرّف على صاحبه وهو يُكتب، وشاشةُ
 * العميل أثناء المكالمة — بياناته وإجراءاته والمؤقّت — حتى الإنهاء الذي
 * لا يمرّ إلا عبر نتيجة.
 *
 * ## أين يخرج الصوت — بصراحة
 *
 * لا مزوّد اتصالات سحابيًّا مربوطًا بعد، فزرّ الاتصال يفتح الرقم على
 * جهاز الموظف ويبدأ الجلسة هنا: المؤقّت والتعريف والملاحظات والنتيجة
 * والنشاط في ملف العميل. في تطبيق سطح المكتب على ويندوز يُطلب الهاتفُ
 * المقترن بالبلوتوث مباشرة — تتّصل من الضغطة، وتُنهى وتُكتم من هنا،
 * وإقفالُها من الهاتف يفتح نافذةَ النتيجة. وفي المتصفّح `tel:` يسلّم
 * الرقمَ لتطبيق الاتصال. حين يُربط مزوّد عبر طبقة التجريد في الخادم يصير
 * النقل نفسه من المتصفح — والشاشة هذه لا تتغيّر.
 */

interface ActiveCall {
  id: string;
  direction: "OUTBOUND" | "INBOUND";
  phone: string;
  startedAt: string;
  /** لحظةُ رفعِ السمّاعة — تُختم من الشاشة حين يُبلّغ الخطُّ «Talking». */
  answeredAt: string | null;
  status: string;
  leadId: string | null;
  accountId: string | null;
  contactId: string | null;
  lead: {
    id: string;
    name: string;
    ownerId: string | null;
    status: string;
  } | null;
  account: { id: string; name: string; ownerId: string | null } | null;
  contact: { id: string; name: string } | null;
  transferredToId: string | null;
}

interface Disposition {
  id: string;
  key: string | null;
  label: string;
  kind: "success" | "info" | "warning" | "danger" | "neutral";
  suggestsFollowUp: boolean;
  isActive: boolean;
}

interface IdentifyMatch {
  kind: "lead" | "account" | "contact";
  name: string;
  leadId?: string;
  accountId?: string | null;
  contactId?: string;
  accountName?: string | null;
  ownerId?: string | null;
  status?: string;
}

export function CallConsole() {
  const { session } = useAuth();
  const user = session!.user;
  const toast = useToast();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();

  const [phone, setPhone] = useState(params.get("dial") ?? "");
  const [ending, setEnding] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [mailing, setMailing] = useState<
    false | { subject?: string; body?: string; link?: MailLink; to?: string }
  >(false);
  const [templating, setTemplating] = useState(false);
  const recorder = useShiftRecorder();
  const telRef = useRef<HTMLAnchorElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);

  /* ---------------- الحضور ---------------- */

  const presence = useQuery({
    queryKey: ["cc", "presence"],
    queryFn: () =>
      api<{ items: Array<Record<string, any>> }>("/callcenter/presence"),
    refetchInterval: 10_000,
  });
  const myPresence = presence.data?.items.find((p) => p.userId === user.id);
  const myState: AgentState = (myPresence?.state as AgentState) ?? "OFFLINE";

  const setState = useMutation({
    mutationFn: (state: string) =>
      api("/callcenter/presence", { method: "PATCH", body: { state } }),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ["cc", "presence"] }),
  });

  /* ---------------- مكالمتي الجارية ---------------- */

  const active = useQuery({
    queryKey: ["cc", "active", user.id],
    queryFn: async () => {
      const page = await api<{ items: ActiveCall[] }>(
        `/callcenter/calls${qs({ agentId: user.id, status: "IN_PROGRESS", pageSize: 1 })}`,
      );
      return page.items[0] ?? null;
    },
    refetchInterval: 15_000,
  });
  const call = active.data ?? null;

  /* الهاتف المقترن — يُراقَب ما دامت مكالمةٌ جارية في النظام. */
  const paired = usePairedPhone(!!call);

  /*
   * الشريطُ يبدأ مع الردّ لا مع الطلب — ويُقفل مع الخطّ لا مع النتيجة.
   *
   * ما دام الخطُّ المقترنُ يُخبرنا بحاله فالبدءُ عند «Active»: حين تُرفع
   * السمّاعةُ فعلًا، لا حين يبدأ الرنين. ومن لا خطَّ له يبقى على ظهورِ
   * المكالمة — لا خبرَ عنده أدقّ.
   */
  useEffect(() => {
    if (!call || !recorder.session) return;
    if (paired.available && !paired.answered) return;
    recorder.beginCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call?.id, recorder.session, paired.available, paired.answered]);

  /*
   * ولحظةُ الردِّ تُختم في الخادم — الشاشةُ وحدَها تسمعها من الخطّ.
   * وبها يعدُّ المؤشّرُ وتُقاس المدّةُ في التقارير، فلا يُحسب الرنينُ كلامًا.
   */
  useEffect(() => {
    if (!call || call.answeredAt || !paired.answered) return;
    void api(`/callcenter/calls/${call.id}/answered`, { method: "PATCH" })
      .then(() => queryClient.invalidateQueries({ queryKey: ["cc"] }))
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call?.id, call?.answeredAt, paired.answered]);

  useEffect(() => {
    if (ending) void recorder.haltCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ending]);

  /*
   * أُقفلت من الهاتف (أو أقفلها الطرف الآخر): تُفتح نافذةُ النتيجة وحدها،
   * فلا تبقى المكالمةُ «جارية» في النظام بعد أن انتهت في الواقع.
   */
  useEffect(() => {
    if (paired.endedFromPhone && call && !ending) {
      setEnding(true);
      toast("أُقفلت المكالمة من الهاتف — سجّل نتيجتها");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paired.endedFromPhone]);

  /* ---------------- التعرّف الحي ---------------- */

  const digits = phone.replace(/\D/g, "");
  const [debounced, setDebounced] = useState(digits);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(digits), 350);
    return () => clearTimeout(t);
  }, [digits]);

  const identify = useQuery({
    queryKey: ["cc", "identify", debounced],
    queryFn: () =>
      api<{ match: IdentifyMatch | null }>(
        `/callcenter/identify?phone=${debounced}`,
      ),
    enabled: !call && debounced.length >= 7,
  });
  const match = identify.data?.match ?? null;

  /* ---------------- البدء ---------------- */

  const start = useMutation({
    mutationFn: () =>
      api<ActiveCall>("/callcenter/calls", {
        method: "POST",
        body: {
          direction: "OUTBOUND",
          phone: phone.trim(),
          leadId: params.get("leadId") || undefined,
          accountId: params.get("accountId") || undefined,
          contactId: params.get("contactId") || undefined,
          activityId: params.get("activityId") || undefined,
        },
      }),
    onSuccess: async (created) => {
      /* وسائط الرابط استُهلكت: بقاؤها يعيد الطلب عند كل تحديث. */
      if (params.get("dial")) setParams({}, { replace: true });
      void queryClient.invalidateQueries({ queryKey: ["cc"] });

      /*
       * الصوت من جهاز الموظف. في تطبيق سطح المكتب يُطلب الهاتفُ المقترن
       * مباشرة — ولا يُفتح تطبيقُ الاتصال بديلًا: نافذةُ فون لينك هي ما
       * جئنا نُغني عنه، وإن تعذّر الطلبُ فالرسالةُ تقول ما يُفعل. وفي
       * المتصفّح `tel:` يسلّم الرقمَ لتطبيق الاتصال.
       */
      if (paired.available) {
        const who =
          created.lead?.name ?? created.contact?.name ?? created.account?.name;
        const r = await paired.dial(digits, who ?? undefined);
        if (!r.ok) {
          /* الهاتف لم يطلب الرقم: مكالمةٌ لم تقع لا تبقى «جارية» في النظام. */
          await api(`/callcenter/calls/${created.id}/end`, {
            method: "PATCH",
            body: { status: "CANCELLED" },
          }).catch(() => undefined);
          void queryClient.invalidateQueries({ queryKey: ["cc"] });
          toast(
            r.error === "no-line"
              ? NO_LINE_MESSAGE
              : `تعذّر الاتصال من الهاتف — ${r.error ?? "أعد المحاولة"}`,
          );
        }
        return;
      }
      telRef.current?.click();
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "تعذّر بدء المكالمة"),
  });

  const canDial = digits.length >= 7 && !start.isPending;

  /*
   * التسجيل الملزم لا يحجز الاتصال بزرٍّ منفصل.
   *
   * أوّل ضغطةِ «اتصال» — وهي إيماءةُ المستخدم التي يشترطها المتصفح لطلب
   * الشاشة — تفتح الجلسة تلقائيًّا، ثم تبقى مفتوحةً لكل مكالمةٍ بعدها بلا
   * إعادة سؤال. فمن رفض الإذن لا تبدأ مكالمته (التسجيل شرطٌ)، ومن قبِله
   * مرةً لا يُسأل ثانية.
   */
  const ensureRecording = async (): Promise<boolean> => {
    if (
      (!recorder.policy.callRecording && recorder.policy.mode === "OFF") ||
      recorder.session
    )
      return true;
    const ok = await recorder.startShift();
    if (!ok) {
      toast("التسجيل مُلزَم في شركتك — امنح إذن المشاركة لبدء المكالمة");
    }
    return ok;
  };

  /*
   * في تطبيق سطح المكتب يُسأل الخطُّ قبل أن تُسجَّل المكالمة: هاتفٌ غير
   * موصول يعني لا مكالمة — فلا تُفتح واحدةٌ في النظام ثمّ تُلغى.
   */
  const ensureLine = async (): Promise<boolean> => {
    if (!paired.available) return true;
    if (await paired.ensureLine()) return true;
    toast(NO_LINE_MESSAGE);
    return false;
  };

  const dial = async () => {
    if (!canDial || call) return;
    if (!(await ensureLine())) return;
    if (!(await ensureRecording())) return;
    start.mutate();
  };

  /* اتصالٌ برقمٍ مُختار — من البحث أو من إعادة الاتصال في السجل. */
  /*
   * تجربةُ مكالمةٍ واردة.
   *
   * لا سنترالَ مربوطًا بعد، فيُصنع الوارد هنا كما سيصنعه السنترال: مكالمةٌ
   * واردةٌ برقمٍ عشوائيّ مجهول تُفتح في اللوح فورًا. وبها تُجرَّب الدورةُ
   * كلُّها — يُعرف صاحبُها أو يصير محتملًا، ثم تُنهى بنتيجةٍ وتدخل السجل.
   * ورقمُها عشوائيٌّ عمدًا: رقمٌ ثابتٌ يصير بعد أوّل تجربةٍ معروفًا فلا
   * تُجرَّب معه حالةُ المجهول ثانيةً.
   */
  const simulate = useMutation({
    mutationFn: async () => {
      const num = `+2010${Math.floor(10_000_000 + Math.random() * 89_999_999)}`;
      return api<ActiveCall>("/callcenter/calls", {
        method: "POST",
        body: { direction: "INBOUND", phone: num },
      });
    },
    onSuccess: () => {
      toast("وصلت مكالمةٌ واردة — ردَّ عليها من اللوحة");
      void queryClient.invalidateQueries({ queryKey: ["cc"] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّرت التجربة"),
  });

  const dialNumber = async (num: string) => {
    if (call) return;
    const clean = toAsciiDigits(num);
    setPhone(clean);
    if (clean.replace(/\D/g, "").length < 7 || start.isPending) return;
    if (!(await ensureLine())) return;
    if (!(await ensureRecording())) return;
    start.mutate();
  };

  /*
   * إعادة الاتصال من السجل تملأ الرقم فورًا بلا كتابة، والحقل مُركَّز
   * فتكفي ضغطة Enter أو زرّ «اتصال» لبدئها. لا نطلقها تلقائيًّا عند
   * الوصول: مكالمةٌ تبدأ بلا ضغطةٍ من الموظف مفاجئةٌ لا مريحة، وطلبُ
   * إذن التسجيل — إن كان ملزمًا — يشترط إيماءته أصلًا.
   */
  useEffect(() => {
    const num = params.get("dial");
    if (num) phoneInputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="page cc-page page-fill">
      <PageHead
        title="مركز الاتصال"
        subtitle="اتصل، وتعرّف على المتصل، وسجّل النتيجة — وكل مكالمة تدخل ملف صاحبها"
        actions={
          <span className="row" style={{ gap: "var(--sp-1)" }}>
            {/* مبدّل الحالة — «في مكالمة» يقلبها النظام فلا زرّ لها. */}
            {AGENT_STATE_DEFS.filter((s) =>
              (AGENT_STATES_MANUAL as readonly string[]).includes(s.key),
            ).map((s) => (
              <button
                key={s.key}
                className={`btn btn-sm${myState === s.key ? " btn-primary" : ""}`}
                disabled={myState === "ON_CALL" || setState.isPending}
                onClick={() => setState.mutate(s.key)}
              >
                {s.label}
              </button>
            ))}
            {myState === "ON_CALL" && <Badge kind="info">في مكالمة</Badge>}

            {/* نقرة الإذن الواحدة — وبعدها كل مكالمة تُسجَّل آليًّا. */}
            {recorder.session ? (
              <button
                className="btn btn-sm btn-danger"
                onClick={() => {
                  if (
                    recorder.policy.callRecording &&
                    !window.confirm(
                      "التسجيل ملزم في شركتك — إيقافه يمنع بدء مكالمات جديدة. أوقفه؟",
                    )
                  ) {
                    return;
                  }
                  recorder.stopShift();
                }}
                title="إيقاف جلسة تسجيل المكالمات"
              >
                <span className="cc-rec-dot" aria-hidden="true" />
                إيقاف التسجيل
              </button>
            ) : null}
          </span>
        }
      />

      <DesktopAppNotice context="call" />

      {/* رابط الهاتف — مخفي، تنقره الشيفرة بعد تسجيل البدء. */}
      <a
        ref={telRef}
        href={`tel:${digits}`}
        style={{ display: "none" }}
        aria-hidden="true"
      />

      {recorder.session && recorder.policy.mode !== "OFF" && (
        <div className="alert alert-info mb-4">
          {recorder.policy.mode === "SCREENSHOTS"
            ? `مراقبة الشاشة فعّالة: تُرسل لقطة كل ${recorder.policy.screenshotEverySec} ثانية إلى Drive الشركة.`
            : "مراقبة الشاشة فعّالة: يُسجَّل فيديو الوردية إلى Drive الشركة."}
        </div>
      )}

      <div className="cc-shell fill-rest">
        {/* ================= لوحة الاتصال ================= */}
        <section className="card cc-dialer">
          <div className="card-head">
            <h2 className="card-title">لوحة الاتصال</h2>
          </div>
          <div className="card-body">
            {!call && <DialerSearch onPick={(num) => void dialNumber(num)} />}

            <input
              ref={phoneInputRef}
              className="input cc-phone num"
              dir="ltr"
              inputMode="tel"
              placeholder="01x xxxx xxxx"
              value={phone}
              disabled={!!call}
              onChange={(e) => setPhone(toAsciiDigits(e.target.value))}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") void dial();
                if (e.key === "Backspace" || e.key === "Delete") return;
              }}
              aria-label="رقم الهاتف"
            />

            {/* لوحة الأرقام — لمن يطلب بالفأرة أو من شاشة لمس. */}
            <div className="cc-pad" role="group" aria-label="لوحة الأرقام">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9", "+", "0", "⌫"].map(
                (k) => (
                  <button
                    key={k}
                    type="button"
                    className="cc-key num"
                    disabled={!!call}
                    onClick={() =>
                      k === "⌫"
                        ? setPhone((p) => p.slice(0, -1))
                        : setPhone((p) => p + k)
                    }
                    aria-label={k === "⌫" ? "مسح" : k}
                  >
                    {k}
                  </button>
                ),
              )}
            </div>

            {/* التعرّف الحي: من يُكتب رقمه يظهر اسمه قبل الاتصال. */}
            {!call && match && (
              <div className="cc-match">
                <Icon
                  name={match.kind === "lead" ? "funnel" : "building"}
                  size={15}
                />
                <div className="cc-match-body">
                  <strong>{match.name}</strong>
                  <span className="tiny mute">
                    {match.kind === "lead"
                      ? "عميل محتمل"
                      : match.kind === "contact"
                        ? `جهة اتصال${match.accountName ? ` — ${match.accountName}` : ""}`
                        : "عميل"}
                  </span>
                </div>
                <Link
                  className="link tiny"
                  to={
                    match.leadId
                      ? `/crm/leads/${match.leadId}`
                      : `/crm/accounts/${match.accountId}`
                  }
                >
                  الملف
                </Link>
              </div>
            )}
            {!call && debounced.length >= 7 && identify.isSuccess && !match && (
              <p className="tiny mute cc-match-none">
                رقم غير معروف — سيُسجَّل بلا ربط.
              </p>
            )}

            <div className="row mt-3" style={{ gap: "var(--sp-2)" }}>
              <button
                className="btn btn-primary cc-call-btn"
                disabled={!canDial || !!call}
                onClick={() => void dial()}
              >
                <Icon name="phone" size={16} />
                اتصال
              </button>
            </div>

            <p className="tiny mute mt-2">
              {recorder.policy.callRecording && !recorder.session
                ? "التسجيل مُلزَم: أول اتصال سيطلب إذن المشاركة مرة واحدة، ثم يبقى لكل المكالمات."
                : paired.ready
                  ? `الاتصال من هاتفك المقترن (${paired.line?.name}) مباشرةً من الضغطة — والإنهاء والكتم من هنا.`
                  : paired.available
                    ? "لا هاتف مقترنًا بالبلوتوث الآن — الاتصال سيفتح تطبيق الاتصال على الجهاز."
                    : "الاتصال يفتح من جهازك — هاتف الشركة المقترن عبر فون لينك — وتُسجَّل المكالمة هنا."}{" "}
              <Link to="/settings/callcenter" className="link">
                إعدادات الاتصال
              </Link>
            </p>
          </div>
        </section>

        {/* ================= شاشة العميل / السجل ================= */}
        <section className="cc-main">
          {call ? (
            <ActiveCallScreen
              call={call}
              paired={paired}
              onEnd={() => {
                /* «إنهاء وتسجيل النتيجة» يُقفل الخطَّ فورًا ثمّ تُسجَّل النتيجة — لا تبقى المكالمةُ مفتوحةً تحت النافذة. */
                if (paired.live) void paired.end();
                setEnding(true);
              }}
              onTransfer={() => setTransferring(true)}
              onMail={() => setMailing({})}
              onTemplates={() => setTemplating(true)}
            />
          ) : (
            <>
              <UpcomingCalls onDial={(num) => void dialNumber(num)} />
              <RecentCalls
                onRedial={(num) => void dialNumber(num)}
                onSimulate={() => simulate.mutate()}
                simulating={simulate.isPending}
              />
            </>
          )}
        </section>
      </div>

      {ending && call && (
        <EndCallDialog
          call={call}
          onClose={() => setEnding(false)}
          onEnded={(status) => {
            setEnding(false);
            setPhone("");
            /* سُجّلت النتيجة والخطُّ ما زال مفتوحًا على الهاتف: يُقفل. */
            if (paired.live) void paired.end();
            void recorder.finishCall(call.id, status);
            void queryClient.invalidateQueries({ queryKey: ["cc"] });
          }}
        />
      )}

      {transferring && call && (
        <TransferDialog
          callId={call.id}
          onClose={() => setTransferring(false)}
          onDone={() => {
            setTransferring(false);
            void queryClient.invalidateQueries({ queryKey: ["cc"] });
          }}
        />
      )}

      {mailing && (
        <MailCompose
          to={mailing.to ?? ""}
          subject={mailing.subject}
          body={mailing.body}
          link={
            mailing.link ??
            (call
              ? {
                  leadId: call.leadId,
                  accountRefId: call.accountId,
                  contactId: call.contactId,
                }
              : undefined)
          }
          onClose={() => setMailing(false)}
        />
      )}

      {templating && call && (
        <TemplatePicker
          call={call}
          onClose={() => setTemplating(false)}
          onEmail={(subject, body) => {
            setTemplating(false);
            setMailing({ subject, body });
          }}
        />
      )}
    </div>
  );
}

/* ==========================================================================
   البحث في جهات الاتصال — اتصل بالاسم لا بالرقم
   ========================================================================== */

interface SearchHit {
  kind: "contact" | "lead" | "account";
  id: string;
  name: string;
  phone: string | null;
  sub: string | null;
}

function DialerSearch({ onPick }: { onPick: (phone: string) => void }) {
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 300);
    return () => clearTimeout(t);
  }, [term]);

  /*
   * يبحث في جهات الاتصال والعملاء المحتملين معًا — بالاسم أو الرقم.
   * هؤلاء من لهم أرقام يُتّصل بها، والحساب يُطلب عبر جهاته لا مباشرةً.
   */
  const results = useQuery({
    queryKey: ["cc", "dialer-search", debounced],
    queryFn: async () => {
      const [contacts, leads] = await Promise.all([
        api<{ items: Array<Record<string, any>> }>(
          `/crm/contacts${qs({ q: debounced, pageSize: 6 })}`,
        ),
        api<{ items: Array<Record<string, any>> }>(
          `/crm/leads${qs({ q: debounced, pageSize: 6 })}`,
        ),
      ]);
      const hits: SearchHit[] = [
        ...contacts.items.map((c) => ({
          kind: "contact" as const,
          id: c.id,
          name: c.name,
          phone: c.phone ?? null,
          sub: c.accountName ?? c.jobTitle ?? null,
        })),
        ...leads.items.map((l) => ({
          kind: "lead" as const,
          id: l.id,
          name: l.name,
          phone: l.phone ?? null,
          sub: l.company ?? "عميل محتمل",
        })),
      ];
      /* من لا رقم له لا يُتّصل به — يسقط من نتائج لوحة الاتصال. */
      return hits.filter((h) => h.phone);
    },
    enabled: debounced.length >= 2,
  });

  const hits = results.data ?? [];

  return (
    <div
      className="cc-search"
      onBlur={() => setTimeout(() => setOpen(false), 150)}
    >
      <div className="cc-search-box">
        <Icon name="search" size={15} />
        <input
          className="cc-search-input"
          placeholder="ابحث بالاسم أو الرقم في جهات الاتصال"
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          aria-label="بحث في جهات الاتصال"
        />
        {term && (
          <button
            className="cc-search-clear"
            onClick={() => {
              setTerm("");
              setOpen(false);
            }}
            aria-label="مسح"
          >
            <Icon name="x" size={13} />
          </button>
        )}
      </div>

      {open && debounced.length >= 2 && (
        <div className="cc-search-list">
          {results.isPending && (
            <div className="cc-search-empty tiny mute">جارٍ البحث…</div>
          )}
          {results.isSuccess && hits.length === 0 && (
            <div className="cc-search-empty tiny mute">لا نتائج لها رقم.</div>
          )}
          {hits.map((h) => (
            <button
              key={`${h.kind}:${h.id}`}
              className="cc-search-hit"
              onMouseDown={(e) => {
                /* mouseDown قبل blur: النقر يصل قبل أن تُغلق القائمة. */
                e.preventDefault();
                setTerm("");
                setOpen(false);
                onPick(h.phone!);
              }}
            >
              <Icon name={h.kind === "lead" ? "funnel" : "user"} size={14} />
              <span className="cc-search-hit-body">
                <strong>{h.name}</strong>
                {h.sub && <span className="tiny mute"> — {h.sub}</span>}
              </span>
              <span className="tiny mute num" dir="ltr">
                {h.phone}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
   شاشة العميل أثناء المكالمة
   ========================================================================== */

function ActiveCallScreen({
  call,
  paired,
  onEnd,
  onTransfer,
  onMail,
  onTemplates,
}: {
  call: ActiveCall;
  paired: PairedPhone;
  onEnd: () => void;
  onTransfer: () => void;
  onMail: () => void;
  onTemplates: () => void;
}) {
  const customerName =
    call.lead?.name ?? call.contact?.name ?? call.account?.name ?? null;
  const recordLink = call.leadId
    ? `/crm/leads/${call.leadId}`
    : call.accountId
      ? `/crm/accounts/${call.accountId}`
      : null;
  const wa = whatsappNumber(call.phone);

  return (
    <div className="card cc-active">
      <div className="cc-active-head">
        <span className="cc-pulse" aria-hidden="true" />
        <div className="cc-active-who">
          <strong>{customerName ?? call.phone}</strong>
          <span className="tiny mute num" dir="ltr">
            {call.phone}
          </span>
        </div>
        {/* «يرنّ…» حتى يُعرف الردّ: من الخطّ المقترن، أو من الموظف في المتصفح. */}
        <CallTimer since={call.startedAt} answeredAt={call.answeredAt} ringing />
      </div>

      <div className="card-body">
        {/* الخطُّ نفسه — من تطبيق سطح المكتب: حالةُ الهاتف وأزراره. */}
        {paired.available && <PhoneBar paired={paired} onHangUp={onEnd} />}

        {!paired.available && !call.answeredAt && <ConfirmConnected call={call} />}

        <div
          className="row mb-3"
          style={{ gap: "var(--sp-2)", flexWrap: "wrap" }}
        >
          {call.lead && <Badge kind="info">عميل محتمل</Badge>}
          {call.account && <Badge kind="success">عميل</Badge>}
          {call.transferredToId && <Badge kind="warning">محوَّلة</Badge>}
          {recordLink && (
            <Link className="link small" to={recordLink} target="_blank">
              افتح الملف الكامل في تبويب — يظهر فيه سجلّ التعامل والمهام
              المفتوحة
            </Link>
          )}
          {!customerName && (
            <span className="tiny mute">
              رقم غير معروف — اربطه من ملف العميل بعد الإنهاء.
            </span>
          )}
        </div>

        {(call.lead || call.account) && (
          <div className="cc-owner mb-3">
            <span className="tiny mute">المسؤول عن الحساب:</span>
            <OwnerChip
              ownerId={call.lead?.ownerId ?? call.account?.ownerId ?? null}
              size={22}
            />
          </div>
        )}

        {/* إجراءات أثناء المكالمة — كلها تعمل والخط مفتوح. */}
        <div className="cc-actions">
          <button className="btn" onClick={onMail}>
            <Icon name="mail" size={15} />
            أرسل بريدًا
          </button>
          <button className="btn" onClick={onTemplates}>
            <Icon name="zap" size={15} />
            رسالة جاهزة
          </button>
          {wa && (
            <a
              className="btn"
              href={`https://wa.me/${wa}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon name="chat" size={15} />
              واتساب
            </a>
          )}
          <button className="btn" onClick={onTransfer}>
            <Icon name="arrowRightLeft" size={15} />
            حوّل المكالمة
          </button>
          <button className="btn btn-danger" onClick={onEnd}>
            <Icon name="phone" size={15} />
            إنهاء وتسجيل النتيجة
          </button>
        </div>

        <p className="tiny mute mt-3">
          الملاحظات تُكتب في نافذة الإنهاء وتدخل سجلّ التعامل تلقائيًّا — لا
          تسجّل المكالمة مرة ثانية من ملف العميل.
        </p>
      </div>
    </div>
  );
}

/**
 * شريطُ الهاتف المقترن — الخطُّ في يد الموظّف من الشاشة.
 *
 * كتمٌ وانتظارٌ وإقفال، وحالةُ المكالمة كما يبلّغها الهاتف (يتّصل، جارية،
 * على الانتظار). الإقفالُ من هنا يُقفل الخطَّ على الهاتف ثمّ يفتح نافذةَ
 * النتيجة — الخطوةُ نفسها التي كانت تحتاج نافذةَ فون لينك ثمّ العودة.
 */
export function PhoneBar({
  paired,
  onHangUp,
  compact = false,
}: {
  paired: PairedPhone;
  onHangUp: () => void;
  compact?: boolean;
}) {
  const c = paired.call;
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  /*
   * إخفاقُ الأمرِ يُقال.
   *
   * كان الجوابُ يُرمى، فيضغط الموظّفُ «كتم» ولا يحدثُ شيءٌ ولا يُقال له
   * لماذا — فيظنّ الزرَّ معطّلًا ويعيد. وويندوز يرفضُ بعضَ الأوامرِ على
   * الخطّ المقترن بحسب حالِ المكالمةِ وجهازِ صوتها، وذاك خبرٌ لا يُكتم.
   */
  const run = async (fn: () => Promise<PhoneReply>, what: string) => {
    setBusy(true);
    try {
      const r = await fn();
      if (!r.ok) toast(`تعذّر ${what} من الشاشة — ${r.error ?? "رفضه ويندوز"}`);
      return r;
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`cc-phone-bar${compact ? " is-compact" : ""} mb-3`}>
      <span className="cc-phone-bar-status">
        <Icon name="phone" size={14} />
        {/*
          مكالمةٌ قائمةٌ لا يكشفها ويندوز: لا تُنكَر لأنّ الخطَّ صمت.
          «جارية» تُقال بشهادة النافذة، وكتمُها وانتظارُها وحدَهما ما
          يتعذّر — فهما من الخطّ ولا خطَّ يُبلّغ.
        */}
        {c
          ? phoneStatusLabel(c.status)
          : paired.live
            ? paired.answered
              ? "جارية"
              : "يرنّ…"
            : paired.ready
              ? "لا مكالمة على الهاتف"
              : "الهاتف غير موصول"}
        {!compact && paired.line && (
          <span className="tiny mute"> — {paired.line.name}</span>
        )}
      </span>
      <span className="cc-phone-bar-actions">
        {/*
          الكتمُ يعملُ ولو لم يكشف ويندوزُ المكالمة: يُكتم ميكروفونُ
          الجهاز، وهو ميكروفونُها حين يكون صوتُها على الحاسب. فلا يُقفل
          الزرُّ على `c` وحدَها كما كان — كان يُعطَّل والمكالمةُ قائمة.
        */}
        <button
          className={`btn btn-sm${paired.muted ? " btn-primary" : ""}`}
          disabled={!paired.live || busy || paired.muteUnavailable}
          onClick={() =>
            void run(
              () => (paired.muted ? paired.unmute() : paired.mute()),
              paired.muted ? "إلغاء الكتم" : "الكتم",
            )
          }
          title={
            paired.muteUnavailable
              ? "الكتم غير متاح على ويندوز ١٠ والصوت على الموبايل"
              : paired.muted
                ? "إلغاء الكتم"
                : "كتم الميكروفون"
          }
        >
          <Icon name={paired.muted ? "micOff" : "mic"} size={14} />
          {paired.muted ? "مكتوم" : "كتم"}
        </button>
        <button
          className={`btn btn-sm${c?.status === "Held" ? " btn-primary" : ""}`}
          disabled={!c || busy}
          /* الانتظارُ من الخطّ وحدَه، وويندوز ١٠ لا يكشف الخطّ — وفون لينك نفسُه بلا زرِّ انتظار. */
          onClick={() =>
            void run(
              () => (c?.status === "Held" ? paired.resume() : paired.hold()),
              c?.status === "Held" ? "الاستئناف" : "الانتظار",
            )
          }
          title={
            paired.legacy
              ? "الانتظار غير متاح على ويندوز ١٠"
              : c?.status === "Held"
                ? "استئناف"
                : "انتظار"
          }
        >
          <Icon name={c?.status === "Held" ? "play" : "pause"} size={14} />
          {c?.status === "Held" ? "استئناف" : "انتظار"}
        </button>
        <button
          className="btn btn-sm btn-danger"
          disabled={busy}
          onClick={() =>
            void (async () => {
              setBusy(true);
              try {
                /*
                 * والإقفالُ يمضي وإن رفضه الخطّ: أكثرُ ما يرفضُه أنّها
                 * أُقفلت من الهاتفِ قبلَه — فالنتيجةُ تُسجَّل على كلّ حال،
                 * ولا يُزعج الموظّفُ برفضٍ لا يعنيه.
                 */
                if (paired.live) await paired.end();
              } finally {
                setBusy(false);
              }
              onHangUp();
            })()
          }
          title="إقفال الخط على الهاتف وتسجيل النتيجة"
        >
          <Icon name="phoneOff" size={14} />
          إقفال
        </button>
      </span>
    </div>
  );
}

/**
 * عدّادُ المكالمة — من الردِّ لا من الطلب.
 *
 * كان يعدُّ من لحظةِ الضغطِ على «اتصال»، فيقرأ الموظّفُ دقيقةً وهو لم
 * يتكلّم بعدُ إلّا عشرين ثانية. وما قبلَ الردِّ رنينٌ يُقال باسمه.
 */
/**
 * «هل اتّصل هاتفك فعلًا؟» — في المتصفح وحده.
 *
 * من المتصفح يُسلَّم الرقم لتطبيق الاتصال (فون لينك مثلًا) ولا خبر يعود:
 * اتّصل أم تعذّر. فبقيت مكالمةٌ لم تقع «جارية» تسعَ ساعاتٍ لا تُقفل. فالموظف
 * هو من يُخبر: «نعم» تختم لحظة الردّ ويبدأ العدّاد منها، و«لا» تُلغيها بلا
 * أثرٍ في سجلّ العميل. ومن لا يجيب تُلغيها الخادمُ بعد عشر دقائق.
 */
function ConfirmConnected({ call }: { call: ActiveCall }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const done = () => void queryClient.invalidateQueries({ queryKey: ["cc"] });

  const yes = useMutation({
    mutationFn: () => api(`/callcenter/calls/${call.id}/answered`, { method: "PATCH" }),
    onSuccess: done,
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر التأكيد"),
  });
  const no = useMutation({
    mutationFn: () =>
      api(`/callcenter/calls/${call.id}/end`, {
        method: "PATCH",
        body: { status: "CANCELLED" },
      }),
    onSuccess: () => {
      toast("أُلغيت المكالمة — لم تُسجَّل في ملف العميل");
      done();
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر الإلغاء"),
  });

  return (
    <div className="alert alert-warning mb-3">
      <div className="row" style={{ gap: "var(--sp-2)", flexWrap: "wrap", alignItems: "center" }}>
        <span className="small" style={{ flex: 1, minWidth: 200 }}>
          هل بدأ الاتصال فعلًا على هاتفك؟
        </span>
        <button
          className="btn btn-sm btn-primary"
          onClick={() => yes.mutate()}
          disabled={yes.isPending || no.isPending}
        >
          نعم، المكالمة شغّالة
        </button>
        <button
          className="btn btn-sm"
          onClick={() => no.mutate()}
          disabled={yes.isPending || no.isPending}
        >
          لا — ما حصلش اتصال
        </button>
      </div>
    </div>
  );
}

function CallTimer({
  since,
  answeredAt,
  ringing = false,
}: {
  since: string;
  answeredAt?: string | null;
  /** خطٌّ يُبلّغ الردَّ ولم يُبلّغه بعد — وبلا خطٍّ لا رنينَ يُعرف. */
  ringing?: boolean;
}) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  if (ringing && !answeredAt) {
    return (
      <span className="cc-timer num" aria-label="في انتظار الردّ">
        يرنّ…
      </span>
    );
  }

  /* بلا لحظةِ ردٍّ مختومة — لا خطَّ يُبلّغها — يُقاس من الطلب كما كان. */
  const sec = Math.max(
    0,
    Math.floor((Date.now() - new Date(answeredAt ?? since).getTime()) / 1000),
  );
  const mm = String(Math.floor(sec / 60)).padStart(2, "0");
  const ss = String(sec % 60).padStart(2, "0");

  return (
    <span className="cc-timer num" aria-label="مدة المكالمة">
      {mm}:{ss}
    </span>
  );
}

/* ==========================================================================
   نافذة الإنهاء — النتيجة شرط لا زينة
   ========================================================================== */

function EndCallDialog({
  call,
  onClose,
  onEnded,
}: {
  call: ActiveCall;
  onClose: () => void;
  onEnded: (status: string) => void;
}) {
  const toast = useToast();
  const [dispositionId, setDispositionId] = useState("");
  const [note, setNote] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [error, setError] = useState<string | null>(null);

  const dispositions = useQuery({
    queryKey: ["cc", "dispositions"],
    queryFn: () => api<{ items: Disposition[] }>("/callcenter/dispositions"),
    staleTime: 5 * 60_000,
  });
  const items = (dispositions.data?.items ?? []).filter((d) => d.isActive);
  const chosen = items.find((d) => d.id === dispositionId);

  /* اختيار نتيجةٍ تقترح متابعةً يملأ الموعد بثلاثة أيام — ويبقى قابلًا للمسح. */
  useEffect(() => {
    if (chosen?.suggestsFollowUp && !followUp) {
      const d = new Date(Date.now() + 3 * 86_400_000);
      d.setMinutes(0, 0, 0);
      setFollowUp(d.toISOString().slice(0, 16));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispositionId]);

  const end = useMutation({
    mutationFn: (status: "COMPLETED" | "NO_ANSWER" | "CANCELLED") =>
      api(`/callcenter/calls/${call.id}/end`, {
        method: "PATCH",
        body: {
          status,
          dispositionId:
            status === "COMPLETED" ? dispositionId || undefined : undefined,
          note: note.trim() || undefined,
          followUpAt: followUp ? new Date(followUp).toISOString() : undefined,
        },
      }),
    onSuccess: (_res, status) => {
      toast("سُجّلت المكالمة في ملف العميل");
      onEnded(status);
    },
    onError: (e) => setError(e instanceof Error ? e.message : "تعذّر الإنهاء."),
  });

  return (
    <Modal title="نتيجة المكالمة" onClose={onClose}>
      {error && <div className="alert alert-danger mb-3">{error}</div>}

      <div className="cc-disp-grid">
        {items.map((d) => (
          <button
            key={d.id}
            type="button"
            className={`cc-disp${dispositionId === d.id ? " on" : ""}`}
            data-kind={d.kind}
            onClick={() => setDispositionId(d.id)}
            aria-pressed={dispositionId === d.id}
          >
            {d.label}
          </button>
        ))}
      </div>

      <label className="field mt-3">
        <span className="label">ملاحظات — تدخل سجلّ التعامل</span>
        <textarea
          className="textarea"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="ما قاله العميل، وما وُعد به…"
        />
      </label>

      <label className="field">
        <span className="label">
          متابعة{chosen?.suggestsFollowUp ? "" : " (اختياري)"} — تُنشأ مهمةً
          بموعدها
        </span>
        <input
          className="input"
          type="datetime-local"
          value={followUp}
          onChange={(e) => setFollowUp(e.target.value)}
        />
      </label>

      <div
        className="row mt-4"
        style={{ gap: "var(--sp-2)", flexWrap: "wrap" }}
      >
        <button
          className="btn btn-primary"
          disabled={!dispositionId || end.isPending}
          onClick={() => end.mutate("COMPLETED")}
        >
          تمّت — سجّل
        </button>
        <button
          className="btn"
          disabled={end.isPending}
          onClick={() => end.mutate("NO_ANSWER")}
        >
          لم يُرَدّ
        </button>
        {/* الملغاة لا تدخل سجل التعامل — نقرة خاطئة ليست تاريخ تواصل. */}
        <button
          className="btn btn-ghost"
          disabled={end.isPending}
          onClick={() => end.mutate("CANCELLED")}
        >
          إلغاء (خطأ في الطلب)
        </button>
      </div>
    </Modal>
  );
}

/* ==========================================================================
   التحويل
   ========================================================================== */

function TransferDialog({
  callId,
  onClose,
  onDone,
}: {
  callId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { session } = useAuth();
  const user = session!.user;
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);

  const team = useQuery({
    queryKey: ["team", "directory"],
    queryFn: () =>
      api<Array<{ userId: string; name: string }>>("/team/members"),
  });

  const transfer = useMutation({
    mutationFn: (toUserId: string) =>
      api(`/callcenter/calls/${callId}/transfer`, {
        method: "PATCH",
        body: { toUserId },
      }),
    onSuccess: () => {
      toast("حُوّلت — وصله إشعار فوري");
      onDone();
    },
    onError: (e) => setError(e instanceof Error ? e.message : "تعذّر التحويل."),
  });

  return (
    <Modal title="تحويل المكالمة" onClose={onClose}>
      {error && <div className="alert alert-danger mb-3">{error}</div>}
      <p className="tiny mute mb-3">
        يصل المحوَّلَ إليه إشعارٌ فوري، وتبقى المكالمة في سجلّك أنت — أنت من
        بدأها.
      </p>
      <div className="pick-list">
        {(team.data ?? [])
          .filter((m) => m.userId !== user.id)
          .map((m) => (
            <button
              key={m.userId}
              className="pick-row"
              disabled={transfer.isPending}
              onClick={() => transfer.mutate(m.userId)}
            >
              <OwnerChip ownerId={m.userId} size={24} />
            </button>
          ))}
      </div>
    </Modal>
  );
}

/* ==========================================================================
   المكالمات القادمة — أنشطة مكالماتٍ مجدولة من إدارة العملاء
   ========================================================================== */

/**
 * ما خطّطه الفريق من ملفات العملاء يظهر حيث يُنفَّذ: في الكونسول.
 *
 * كل صفٍّ نشاطُ مكالمةٍ مجدول برقم صاحبه، وزرُّه يملأ لوحة الاتصال.
 * وحين تتم مكالمةٌ فعلية لنفس السجل يُعلَّم النشاط منجَزًا من الخادم —
 * فالقائمة تفرغ بالعمل لا بالنسيان.
 */
function UpcomingCalls({ onDial }: { onDial: (phone: string) => void }) {
  const upcoming = useQuery({
    queryKey: ["crm", "upcoming-calls"],
    queryFn: () =>
      api<{ items: Array<Record<string, any>> }>(
        `/crm/activities${qs({ type: "CALL", status: "PLANNED", sort: "dueAt", order: "asc", pageSize: 8 })}`,
      ),
    refetchInterval: 60_000,
  });

  const items = upcoming.data?.items ?? [];
  if (upcoming.isSuccess && items.length === 0) return null;

  const now = Date.now();

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="card-title">المكالمات القادمة</h2>
        <Link to="/crm/activities?type=CALL" className="link small">
          كل الأنشطة
        </Link>
      </div>
      <div className="card-body">
        {upcoming.isPending && (
          <div className="skeleton" style={{ height: 80 }} />
        )}
        <div className="col" style={{ gap: 0 }}>
          {items.map((a) => {
            const phone =
              a.lead?.phone ?? a.contact?.phone ?? a.account?.phone ?? null;
            const who =
              a.lead?.name ?? a.contact?.name ?? a.account?.name ?? null;
            const overdue = a.dueAt && new Date(a.dueAt).getTime() < now;
            return (
              <div key={a.id} className="cc-recent-row">
                <Icon name="phone" size={14} />
                <span className="cc-recent-who" title={a.subject}>
                  {a.subject}
                  {who && <span className="tiny mute"> · {who}</span>}
                </span>
                {a.dueAt && (
                  <span
                    className="tiny mute nowrap"
                    style={{ color: overdue ? "var(--danger)" : undefined }}
                  >
                    {overdue ? "فات موعدها — " : ""}
                    {relative(a.dueAt)}
                  </span>
                )}
                <span style={{ marginInlineStart: "auto" }}>
                  {phone ? (
                    <button
                      className="btn btn-sm"
                      onClick={() => onDial(phone)}
                      title={`املأ اللوحة بـ${phone}`}
                    >
                      <Icon name="phone" size={13} />
                      اتصل
                    </button>
                  ) : (
                    <span className="tiny mute">بلا رقم مسجَّل</span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   مكالماتي الأخيرة — حين لا مكالمة جارية
   ========================================================================== */

/**
 * مكالماتُك الأخيرة — واردةً وصادرة في قائمةٍ واحدة.
 *
 * لوحٌ للوارد وحده كان يقسم ما لا ينقسم في ذهن من يعمل: هو يتابع
 * «آخرَ ما جرى» لا اتجاهَه. فالاتجاهُ أيقونةٌ في أوّل الصفّ، والصفُّ نفسُه
 * واحد. وما جاء من رقمٍ لا يعرفه النظام يحمل زرَّ «عميل محتمل» — فأثمنُ
 * ما في اليوم عميلٌ طرق البابَ بنفسه، ولا يضيع في سطرٍ يُقرأ ويُنسى.
 */
function RecentCalls({
  onRedial,
  onSimulate,
  simulating,
}: {
  onRedial: (phone: string) => void;
  onSimulate: () => void;
  simulating: boolean;
}) {
  const { session } = useAuth();
  const user = session!.user;
  const toast = useToast();
  const queryClient = useQueryClient();

  const recent = useQuery({
    queryKey: ["cc", "recent", user.id],
    queryFn: () =>
      api<{ items: Array<Record<string, any>>; total: number }>(
        `/callcenter/calls${qs({ agentId: user.id, pageSize: 10 })}`,
      ),
    refetchInterval: 30_000,
  });

  const items = recent.data?.items ?? [];

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="card-title">مكالماتك الأخيرة</h2>
        <span className="row" style={{ gap: 8, marginInlineStart: "auto" }}>
          {/*
            زرُّ التجربة يصنع مكالمةً واردةً حقيقيةً برقمٍ مجهول، فتُجرَّب
            الدورةُ كلُّها: ترنّ، فتُردّ، فيصير صاحبُها محتملًا، فتُنهى
            بنتيجةٍ وتدخل السجل. ولا سنترالَ مربوطًا بعد، فهذا بابُ تجربته.
          */}
          <button
            type="button"
            className="btn btn-sm"
            disabled={simulating}
            onClick={onSimulate}
            title="يُنشئ مكالمةً واردةً برقمٍ مجهول لتجربة الدورة كاملة"
          >
            <Icon name="zap" size={13} />
            {simulating ? "جارٍ…" : "تجربة مكالمة واردة"}
          </button>
          <Link to="/callcenter/history" className="link small">
            السجل الكامل
          </Link>
        </span>
      </div>
      <div className="card-body">
        {recent.isPending && (
          <div className="skeleton" style={{ height: 160 }} />
        )}
        {items.length === 0 && recent.isSuccess && (
          <EmptyState
            title="لا مكالمات بعد"
            text="اكتب رقمًا في لوحة الاتصال — إن كان لعميلٍ معروف يظهر اسمه قبل أن تتصل."
          />
        )}
        <div className="col" style={{ gap: 0 }}>
          {items.map((c) => {
            const email =
              c.lead?.email ?? c.contact?.email ?? c.account?.email ?? null;
            const link: MailLink = {
              leadId: c.leadId,
              accountRefId: c.accountId,
              contactId: c.contactId,
            };
            const who = identityOf(c as CallIdentityRow);
            const live = c.status === "RINGING" || c.status === "IN_PROGRESS";
            return (
              <div
                key={c.id}
                className={`cc-recent-row${live ? " is-live" : ""}`}
              >
                {/* الاتجاهُ أيقونةٌ في أوّل الصفّ: صادرٌ يخرج ووارد يدخل. */}
                <CallDirection direction={c.direction} />

                {/* الاسمُ وتبعيّتُه — وجهةُ الاتصال باسم حسابها. */}
                <span className="cc-recent-who cc-who">
                  {who.link ? (
                    <Link
                      to={who.link}
                      className="link"
                      title="افتح الملف الكامل"
                    >
                      {who.name}
                    </Link>
                  ) : (
                    <span className="num" dir="ltr">
                      {who.name ?? c.phone}
                    </span>
                  )}
                  {who.belongsTo && (
                    <span className="tiny mute">{who.belongsTo}</span>
                  )}
                </span>

                {c.disposition && (
                  <Badge kind={c.disposition.kind}>{c.disposition.label}</Badge>
                )}

                <span
                  className="tiny mute"
                  title={time(c.startedAt)}
                  style={{ marginInlineStart: "auto" }}
                >
                  {relative(c.startedAt)}
                </span>

                {/*
                  الإجراءات هي أيقونات الوصول الموحّدة: اتصال (يملأ اللوحة
                  هنا لا يُبحر)، وواتساب داخل قنوات ميتا، وبريد من صندوق
                  الشركة، وSMS من تطبيق الجهاز.
                */}
                {/* المجهولُ يصير محتملًا بنقرة — والمعروفُ تُرى قنواتُه. */}
                {!who.name && <AddCallerButton call={c as CallIdentityRow} />}
                <ReachIcons
                  name={who.name ?? c.phone}
                  phone={c.phone}
                  email={email}
                  link={link}
                  size={14}
                  onCall={() => onRedial(c.phone)}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   الرسائل الجاهزة — «ابعتلي العرض» تصير نقرة
   ========================================================================== */

function TemplatePicker({
  call,
  onClose,
  onEmail,
}: {
  call: ActiveCall;
  onClose: () => void;
  onEmail: (subject: string | undefined, body: string) => void;
}) {
  const templates = useQuery({
    queryKey: ["cc", "templates"],
    queryFn: () =>
      api<{
        items: Array<{
          id: string;
          name: string;
          channel: "EMAIL" | "SMS" | "WHATSAPP" | "MESSENGER" | "INSTAGRAM";
          subject: string | null;
          body: string;
          isActive: boolean;
        }>;
      }>("/callcenter/templates"),
    staleTime: 5 * 60_000,
  });

  const vars = {
    name: call.lead?.name ?? call.contact?.name ?? call.account?.name ?? null,
    company: call.account?.name ?? null,
  };
  const wa = whatsappNumber(call.phone);
  const navigate = useNavigate();
  const toast = useToast();

  /* واتساب: هل للرقم محادثةٌ قائمة في قنوات ميتا؟ فتُفتح داخل النظام. */
  const metaConv = useQuery({
    queryKey: ["meta", "by-phone", wa],
    queryFn: () =>
      api<{ conversationId: string | null }>(
        `/meta/by-phone${qs({ phone: wa })}`,
      ),
    enabled: !!wa,
  });

  const items = (templates.data?.items ?? []).filter((t) => t.isActive);

  /*
   * قنوات ميتا (واتساب/ماسنجر/إنستغرام) تُفتح داخل النظام لا في تطبيقٍ
   * خارجي: الردّ من قنواتنا يُحفظ في سجل العميل. واتساب يُطابَق بالرقم
   * فيُفتح على محادثته إن وُجدت؛ وماسنجر وإنستغرام لا رقم لهما يُطابَق،
   * فتُفتح صفحة القنوات ويُنسخ النص ليُلصق على المحادثة الصحيحة.
   */
  const sendVia = (t: (typeof items)[number], filled: string) => {
    if (t.channel === "EMAIL") {
      onEmail(t.subject ? fillTemplate(t.subject, vars) : undefined, filled);
      return;
    }
    if (t.channel === "SMS") {
      /* SMS بلا بوابة مركزية: يفتح تطبيق الرسائل على الجهاز بالنص جاهزًا. */
      window.open(
        `sms:${call.phone}?body=${encodeURIComponent(filled)}`,
        "_self",
      );
      onClose();
      return;
    }
    /* بقيّة القنوات قنوات ميتا. */
    void navigator.clipboard?.writeText(filled).catch(() => undefined);
    if (t.channel === "WHATSAPP" && metaConv.data?.conversationId) {
      navigate(`/crm/channels?conversation=${metaConv.data.conversationId}`);
    } else if (t.channel === "WHATSAPP" && wa) {
      window.open(
        `https://wa.me/${wa}?text=${encodeURIComponent(filled)}`,
        "_blank",
        "noopener",
      );
    } else {
      /* ماسنجر/إنستغرام: صفحة القنوات، والنصّ في الحافظة. */
      navigate("/crm/channels");
      toast("فُتحت قنوات ميتا والنصّ مُنسَخ — الصقه على محادثة العميل");
    }
    onClose();
  };

  return (
    <Modal title="رسالة جاهزة" onClose={onClose}>
      <p className="tiny mute mb-3">
        {"{{الاسم}}"} مُلئ باسم العميل. البريد يُفتح في المنشئ، وقنوات ميتا
        تُفتح داخل النظام، وSMS يفتح تطبيق الرسائل على جهازك.
      </p>
      <div className="pick-list">
        {items.length === 0 && (
          <p className="mute center">
            لا قوالب — أنشئها من إعدادات مركز الاتصال.
          </p>
        )}
        {items.map((t) => {
          const filled = fillTemplate(t.body, vars);
          /* واتساب بلا مفتاح دولة لا يصلح؛ بقيّة القنوات تعمل دائمًا. */
          const disabled =
            t.channel === "WHATSAPP" && !wa && !metaConv.data?.conversationId;
          const def = TEMPLATE_CHANNEL_MAP[t.channel];
          return (
            <button
              key={t.id}
              className="pick-row"
              disabled={disabled}
              title={
                disabled
                  ? "رقم بلا مفتاح دولة — لا يصلح لواتساب"
                  : filled.slice(0, 120)
              }
              onClick={() => sendVia(t, filled)}
            >
              <Badge kind={def?.kind ?? "neutral"}>
                {def?.label ?? t.channel}
              </Badge>
              <span className="pick-row-text">
                <strong>{t.name}</strong>
                <span className="tiny mute"> — {filled.slice(0, 60)}</span>
              </span>
            </button>
          );
        })}
      </div>
    </Modal>
  );
}
